// @flow
import { memoizedImmutable } from 'common/utils/Memoizer';
import moment from 'moment-timezone';
import * as Immutable from 'immutable';
import { BreakTypeEnum } from 'common/enums/VisitEnums';
import type { List, Map } from 'immutable';
import type { ColorType } from 'common/utils/TourColorDecider';
import type { FpBeat } from 'common/models/internal/FpBeat';
import type { BeatVisit } from 'common/models/internal/BeatVisit';
import { dashboardColors, neoUIColors } from 'common/enums/ColorEnum';
import type { VisitIdOutletType } from 'common/entity/pjpMaster/planView/FpPlanDetailsContainer';
import { FpEventComponentTypes } from 'common/entity/pjpMaster/planView/FpTourEventComponent';

type EventGroupType = {
    groups: List<Object>,
    focusTime: ?number
};

type OperationalShiftEventType = {
    id: string,
    label: ?string,
    color: ColorType,
    startTime: $FixMe,
    endTime: $FixMe,
    type: string
};

type TourBackgroundEventType = {
    id: string,
    label: ?string,
    color: ColorType,
    startTime: $FixMe,
    endTime: $FixMe,
    type: string
};

type EventType = {
    visitIndex: number,
    eventId: string,
    beatId: string,
    userVisitType: string,
    label: string,
    highlight: boolean,
    selected: boolean,
    color: ColorType,
    startTime: $FixMe,
    endTime: $FixMe,
    type: string
};

const UserVisitTypeEnum = Object.freeze({
    RELOAD: 'RELOAD',
    SHIFT_START: 'SHIFT_START',
    SHIFT_END: 'SHIFT_END',
    BREAK: 'BREAK',
    LUNCH: 'LUNCH'
});
export default class FpBeatTimelineHelper {
    static makeEventGroups(
        beats: List<FpBeat>,
        selectedTourIds: List<string>,
        visitIdMap: Map<string, VisitIdOutletType>,
        currentHoveredVisit: ?string,
        hoveredBeat: ?string,
        selectedVisit: ?string,
        selectedBeat: ?string,
        beatSelectionMap: Map<string, boolean>
    ): EventGroupType {
        return FpBeatTimelineHelper.computeInternal(
            beats,
            selectedTourIds,
            visitIdMap,
            currentHoveredVisit,
            hoveredBeat,
            selectedVisit,
            selectedBeat,
            beatSelectionMap
        );
    }

    @memoizedImmutable(100)
    static computeInternal(
        beats: List<FpBeat>,
        selectedTourIds: List<string>,
        visitIdMap: Map<string, VisitIdOutletType>,
        currentHoveredVisit: ?string,
        hoveredBeat: ?string,
        selectedVisit: ?string,
        selectedBeat: ?string,
        beatSelectionMap: Map<string, boolean>
    ): EventGroupType {
        const groups = beats.map((beat, beatIndex) => {
            const isSelected = beatSelectionMap.get(beat.beatId);
            const isHovered =
                beatSelectionMap.get(beat.beatId) &&
                hoveredBeat === beat.beatId;
            const { group } = FpBeatTimelineHelper.createEventGroup(
                beat,
                beatIndex,
                isSelected,
                isHovered,
                selectedVisit,
                selectedBeat,
                currentHoveredVisit
            );
            return group;
        });
        return { groups: Immutable.List(groups), focusTime: null };
    }

    @memoizedImmutable()
    static createEventGroup(
        beat: FpBeat,
        beatIndex: number,
        isSelected: boolean,
        isHovered: boolean,
        selectedVisit: ?string,
        selectedBeat: ?string,
        hoveredVisitId: ?string
    ): List<EventType> {
        const group = Immutable.Map({
            id: beat.beatId,
            isSelected,
            isOverlayVisible: false
        });
        let hasTaskVisit = false;
        const { visits } = beat;
        const [firstVisit] = visits;
        const lastVisit = visits[visits.length - 1];
        let visitIndex = 0;
        const events = visits.map(visit => {
            let shouldHighlight = false;
            const isVisitSelected =
                selectedBeat === beat.beatId &&
                visit.outletVisitId === selectedVisit;
            const { userVisitType } = visit;
            let label = FpBeatTimelineHelper.getVisitLabel(visit);
            if (userVisitType === UserVisitTypeEnum.RELOAD) {
                return FpBeatTimelineHelper.createReloadEvent(
                    visit,
                    label,
                    beat
                );
            }
            if (userVisitType === UserVisitTypeEnum.BREAK) {
                return FpBeatTimelineHelper.createBreakEvent(visit, label);
            } else {
                const isVisitHovered =
                    isHovered && hoveredVisitId === visit.outletVisitId;
                shouldHighlight = isVisitHovered || isVisitSelected;
                if (!label) {
                    visitIndex += 1;
                    label = visitIndex;
                }
                if (visit.outletVisitId) {
                    hasTaskVisit = true;
                }

                return FpBeatTimelineHelper.createEvent(
                    visit,
                    visitIndex,
                    label,
                    neoUIColors.grey500,
                    shouldHighlight,
                    isVisitHovered,
                    isVisitSelected,
                    beat
                );
            }
        });

        const selectedBeatBgColor = isSelected
            ? dashboardColors.white
            : dashboardColors.offWhite;
        const updatedEvents = Immutable.List([
            ...events,
            FpBeatTimelineHelper.createTourBackgroundEvent(
                firstVisit,
                lastVisit,
                selectedBeatBgColor
            )
        ]);

        if (hasTaskVisit) {
            return { group: group.set('events', updatedEvents) };
        } else {
            // If no task visits, set events as empty so that we can render an empty row
            return {
                group: group.set('events', Immutable.List())
            };
        }
    }

    static getVisitLabel(visit: BeatVisit) {
        const eventType = FpBeatTimelineHelper.getEventType(visit);
        switch (eventType) {
            case FpEventComponentTypes.SHIFT_START_VISIT:
                return 'flagMarker';
            case FpEventComponentTypes.SHIFT_END_VISIT:
                return 'circle';
            case FpEventComponentTypes.RELOAD:
                return 'homebase';
            case FpEventComponentTypes.BREAK:
                return 'pause';
            case FpEventComponentTypes.LUNCH:
                return 'lunch';
            case FpEventComponentTypes.REFUEL:
                return 'refuel';
            case FpEventComponentTypes.NIGHT:
                return 'night';
            case FpEventComponentTypes.PICKUP:
            case FpEventComponentTypes.DROP:
            case FpEventComponentTypes.TASK_VISIT:
                return null;
        }
        throw new Error(`Unknown event type: ${eventType}`);
    }

    @memoizedImmutable()
    static createReloadEvent(
        visit: BeatVisit,
        label: string,
        beat: FpBeat
    ): OperationalShiftEventType {
        return Immutable.fromJS({
            id: UserVisitTypeEnum.RELOAD,
            label,
            beatId: beat.beatId,
            color: neoUIColors.grey500,
            visitId: visit.id,
            startTime: moment(visit.eta.startTime).valueOf(),
            endTime: moment(visit.eta.completedTime).valueOf(),
            type: FpEventComponentTypes.RELOAD
        });
    }

    @memoizedImmutable()
    static createOperationShiftEvent(
        visit: BeatVisit,
        color: ColorType
    ): OperationalShiftEventType {
        return Immutable.fromJS({
            id: 'OPERATION_SHIFT',
            label: ' ',
            color,
            startTime: moment(visit.eta.startTime).valueOf(),
            endTime: moment(visit.eta.completedTime).valueOf(),
            type: FpEventComponentTypes.OPERATION_SHIFT
        });
    }

    @memoizedImmutable()
    static createBreakEvent(
        visit: BeatVisit,
        label: string
    ): TourBackgroundEventType {
        return Immutable.fromJS({
            id: UserVisitTypeEnum.BREAK,

            userVisitType: UserVisitTypeEnum.LUNCH,
            label,
            startTime: moment(visit.eta.startTime).valueOf(),
            endTime: moment(visit.eta.completedTime).valueOf(),
            type: FpEventComponentTypes.TOUR_BACKGROUND
        });
    }

    @memoizedImmutable()
    static createTourBackgroundEvent(
        firstVisit: BeatVisit,
        lastVisit: BeatVisit,
        color: ColorType
    ): TourBackgroundEventType {
        return Immutable.fromJS({
            id: 'TOUR_BACKGROUND',
            label: ' ',
            color,
            startTime: moment(firstVisit.eta.startTime).valueOf(),
            endTime: moment(lastVisit.eta.completedTime).valueOf(),
            type: FpEventComponentTypes.TOUR_BACKGROUND
        });
    }

    @memoizedImmutable(10000)
    static createEvent(
        visit: BeatVisit,
        visitIndex: number,
        label: string | number,
        color: ColorType,
        shouldHighlight: boolean,
        isVisitHovered: boolean,
        isSelected: boolean,
        beat: FpBeat
    ): EventType {
        const { id, userVisitType } = visit;
        return Immutable.fromJS({
            visitIndex,
            id: userVisitType || FpEventComponentTypes.TASK_VISIT,
            eventId: id,
            beatId: beat.beatId,
            userVisitType,
            label,
            outletVisitId: visit.outletVisitId,
            highlight: shouldHighlight,
            hovered: isVisitHovered,
            selected: isSelected,
            color,
            breaches: visit.breaches,
            startTime: moment(visit.eta.startTime).valueOf(),
            endTime: moment(visit.eta.completedTime).valueOf(),
            type: visit.userVisitType
        });
    }

    static getEventType(visit: BeatVisit) {
        if (visit.userVisitType && visit.outletVisitId) {
            return FpEventComponentTypes.TASK_VISIT;
        } else if (visit.userVisitType) {
            switch (visit.userVisitType) {
                case UserVisitTypeEnum.SHIFT_START:
                    return FpEventComponentTypes.SHIFT_START_VISIT;
                case UserVisitTypeEnum.SHIFT_END:
                    return FpEventComponentTypes.SHIFT_END_VISIT;
                case UserVisitTypeEnum.RELOAD:
                    return FpEventComponentTypes.RELOAD;
                case UserVisitTypeEnum.BREAK:
                    if (visit.breakType) {
                        switch (visit.breakType) {
                            case BreakTypeEnum.Lunch:
                                return FpEventComponentTypes.LUNCH;
                            case BreakTypeEnum.Refuel:
                                return FpEventComponentTypes.REFUEL;
                            case BreakTypeEnum.Night:
                                return FpEventComponentTypes.NIGHT;
                            default:
                                return FpEventComponentTypes.BREAK;
                        }
                    }
                    return FpEventComponentTypes.BREAK;
            }
        }
        return FpEventComponentTypes.TASK_VISIT;
    }
}
